import type { IUser } from '@condo/domain';
import type { User as FirebaseUser } from 'firebase/auth';
import { Bot, ChevronDown, LogOut, Settings, User, UserRoundX, VenetianMask } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { resolvePersonImageSrcFromUser } from '@/entities/contact';
import { RamAvatar } from '@/shared/components/RamAvatar';
import { Button } from '@/shared/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/shared/components/ui/dropdown-menu';
import { useCurrentUserDetails } from '../model/current-user.hook';
import { useUsers } from '../model/UsersContext';

interface UserMenuProps {
    onLogout: () => void;
    className?: string;
    variant?: 'default' | 'figma';
    user?: FirebaseUser | null;
    /** When set, menu shows this user (bootstrap `IUser`) and offers Unimpersonate. */
    impersonatedUserId?: string | null;
    onUnimpersonate?: () => void;
}

function fullnameForMenuAvatar(user: IUser | null, uid: string): string {
    if (!user) {
        return uid;
    }
    const fromParts = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    return user.displayName?.trim() || fromParts || user.email?.trim() || uid;
}

export function UserMenu({ onLogout, className, variant = 'default', user, impersonatedUserId, onUnimpersonate }: UserMenuProps) {
    const { t } = useTranslation('main');
    const { user: basecampUser, isAdmin } = useCurrentUserDetails();
    const { getUser, getUserName } = useUsers();
    const myContactId = basecampUser?.contact?.contactId;
    const myContactPath = myContactId != null && myContactId > 0 ? `/contacts/${myContactId}` : null;

    const isImpersonating = Boolean(impersonatedUserId && onUnimpersonate);
    const impersonatedUser = isImpersonating && impersonatedUserId ? getUser(impersonatedUserId) : null;
    const impersonatedLabel = isImpersonating && impersonatedUserId ? getUserName(impersonatedUserId, impersonatedUserId, false, true, false) : '';
    const impersonatedEmail = impersonatedUser?.email ?? '';

    // Signed-in Firebase user (header label when not impersonating)
    const getFirebaseUserDisplayName = () => {
        if (user?.displayName) return user.displayName;
        if (user?.email) {
            const emailName = user.email.split('@')[0];
            return emailName
                .split('.')
                .map(part => part.charAt(0).toUpperCase() + part.slice(1))
                .join(' ');
        }
        return 'User';
    };

    const triggerDisplayName = isImpersonating ? impersonatedLabel : getFirebaseUserDisplayName();

    const signedInBootstrapUser = user?.uid ? getUser(user.uid) : null;
    const signedInAvatarFullname = user
        ? signedInBootstrapUser
            ? fullnameForMenuAvatar(signedInBootstrapUser, user.uid)
            : getFirebaseUserDisplayName()
        : 'User';
    const signedInImageSrc = user?.photoURL ?? resolvePersonImageSrcFromUser(signedInBootstrapUser);

    const impersonationTriggerAriaLabel = isImpersonating
        ? `Impersonation active. Viewing as ${triggerDisplayName}.${user?.email ? ` Signed in as ${user.email}.` : ''} Open account menu.`
        : 'Open account menu';

    if (variant === 'figma') {
        const triggerButton = (
            <Button
                variant="ghost"
                className={`flex max-w-full min-w-0 items-center gap-2 sm:gap-3 md:gap-4 hover:bg-transparent p-0 h-auto ${className}`}
                dataTestId="ram-user-menu-trigger"
                aria-label={impersonationTriggerAriaLabel}
            >
                {isImpersonating && impersonatedUserId ? (
                    <span
                        className="flex shrink-0 rounded-full animate-heartbeat-amber-ring motion-reduce:animate-none motion-reduce:ring-2 motion-reduce:ring-amber-400/75 motion-reduce:ring-offset-2 motion-reduce:ring-offset-[#012A3C]"
                        aria-hidden
                    >
                        <RamAvatar
                            imageSrc={resolvePersonImageSrcFromUser(impersonatedUser)}
                            fullname={fullnameForMenuAvatar(impersonatedUser, impersonatedUserId)}
                            size="default"
                        />
                    </span>
                ) : (
                    <span className="flex shrink-0 rounded-full ring-2 ring-white/50" aria-hidden>
                        <RamAvatar imageSrc={signedInImageSrc} fullname={signedInAvatarFullname} size="default" />
                    </span>
                )}
                {isImpersonating ? (
                    <span className="min-w-0 flex-1 self-center">
                        <span className="relative block border-amber-400/35 border-t pt-2">
                            <span
                                role="button"
                                tabIndex={0}
                                className="absolute top-0 left-0 z-[1] flex -translate-y-1/2 cursor-pointer items-center gap-1 rounded-sm bg-[#012A3C] py-0.5 pl-1 pr-1.5 font-['Montserrat'] font-medium text-[9px] text-amber-300 uppercase tracking-[0.14em] outline-none transition-colors hover:text-amber-100 focus-visible:ring-2 focus-visible:ring-amber-300/80 focus-visible:ring-offset-1 focus-visible:ring-offset-[#012A3C]"
                                aria-label="Un-Impersonate — return to your account"
                                onPointerDown={e => {
                                    e.stopPropagation();
                                }}
                                onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onUnimpersonate?.();
                                }}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onUnimpersonate?.();
                                    }
                                }}
                            >
                                <span className="flex size-3 shrink-0 items-center justify-center" aria-hidden>
                                    <UserRoundX className="size-3" strokeWidth={2.25} />
                                </span>
                                <span className="whitespace-nowrap leading-none">Un-Impersonate</span>
                            </span>
                            <span className="block truncate font-['Montserrat'] font-semibold text-base text-white leading-tight tracking-[0.24px]">
                                {triggerDisplayName}
                            </span>
                        </span>
                    </span>
                ) : (
                    <span className="min-w-0 truncate font-['Montserrat'] font-semibold text-base text-white leading-6 tracking-[0.24px]">
                        {triggerDisplayName}
                    </span>
                )}
                <ChevronDown className="h-6 w-6 shrink-0 text-white" />
            </Button>
        );

        return (
            <div className="flex min-w-0 items-center gap-1 sm:gap-2">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-80 p-0" dataTestId="ram-user-menu-content">
                        <div className="p-4">
                            {isImpersonating && impersonatedUserId && onUnimpersonate ? (
                                <div className="mb-4 space-y-3 border-gray-100 border-b pb-4">
                                    <div className="rounded-lg border border-amber-200/90 bg-amber-50 p-3">
                                        <p className="mb-2 flex items-center gap-1.5 font-semibold text-amber-950 text-xs uppercase tracking-wide">
                                            <VenetianMask className="h-3.5 w-3.5 shrink-0 text-amber-800" aria-hidden strokeWidth={2} />
                                            Impersonating
                                        </p>
                                        <div className="flex items-center gap-3">
                                            <RamAvatar
                                                imageSrc={resolvePersonImageSrcFromUser(impersonatedUser)}
                                                fullname={fullnameForMenuAvatar(impersonatedUser, impersonatedUserId)}
                                                size="default"
                                            />
                                            <div className="min-w-0 flex-1">
                                                <h3 className="truncate font-semibold text-gray-900 text-lg">{impersonatedLabel || impersonatedUserId}</h3>
                                                <p className="truncate text-gray-600 text-sm">{impersonatedEmail || '—'}</p>
                                            </div>
                                        </div>
                                        {user?.email ? <p className="mt-2 text-gray-500 text-xs">Signed in as {user.email}</p> : null}
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            className="mt-3 w-full"
                                            onClick={onUnimpersonate}
                                            dataTestId="ram-user-menu-unimpersonate"
                                        >
                                            Unimpersonate
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="mb-4 flex items-center gap-3 border-gray-100 border-b pb-4">
                                    <RamAvatar imageSrc={signedInImageSrc} fullname={signedInAvatarFullname} size="default" />
                                    <div className="min-w-0 flex-1">
                                        <h3 className="font-semibold text-gray-900 text-lg">{getFirebaseUserDisplayName()}</h3>
                                        <p className="text-gray-600 text-sm">{user?.email || t('nav.noEmail')}</p>
                                    </div>
                                </div>
                            )}

                            {/* Menu Items */}
                            <div className="space-y-1">
                                {myContactPath ? (
                                    <Link
                                        to={myContactPath}
                                        data-testid="ram-user-menu-my-profile"
                                        className="flex w-full items-center justify-between rounded-lg p-3 transition-colors duration-200 hover:bg-gray-50"
                                    >
                                        <div className="flex items-center space-x-3">
                                            <div className="rounded-md bg-gray-100 p-2 transition-colors">
                                                <User className="h-4 w-4 text-gray-600" />
                                            </div>
                                            <div className="text-left">
                                                <span className="font-medium text-gray-900">{t('nav.myProfile')}</span>
                                                <p className="mt-1 text-gray-500 text-xs">{t('nav.myProfileDescription')}</p>
                                            </div>
                                        </div>
                                    </Link>
                                ) : (
                                    <div className="flex w-full items-center rounded-lg p-3 text-gray-400">
                                        <div className="flex items-center space-x-3">
                                            <div className="rounded-md bg-gray-100 p-2">
                                                <User className="h-4 w-4" />
                                            </div>
                                            <div className="text-left">
                                                <span className="font-medium text-sm">{t('nav.noContactProfile')}</span>
                                                <p className="mt-1 text-xs">{t('nav.noContactProfileDescription')}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="border-gray-100 border-t pt-3">
                                    <Link
                                        to="/configuration"
                                        data-testid="ram-user-menu-config"
                                        className="flex w-full items-center justify-between rounded-lg p-3 transition-colors duration-200 hover:bg-gray-50"
                                    >
                                        <div className="flex items-center space-x-3">
                                            <div className="rounded-md bg-gray-100 p-2 transition-colors">
                                                <Settings className="h-4 w-4 text-gray-600" />
                                            </div>
                                            <div className="text-left">
                                                <span className="font-medium text-gray-900">{t('nav.config')}</span>
                                                <p className="mt-1 text-gray-500 text-xs">{t('nav.configDescription')}</p>
                                            </div>
                                        </div>
                                    </Link>
                                </div>

                                {isAdmin && (
                                    <Link
                                        to="/agents"
                                        data-testid="ram-user-menu-agent-conversations"
                                        className="flex w-full items-center justify-between rounded-lg p-3 transition-colors duration-200 hover:bg-gray-50"
                                    >
                                        <div className="flex items-center space-x-3">
                                            <div className="rounded-md bg-gray-100 p-2 transition-colors">
                                                <Bot className="h-4 w-4 text-gray-600" />
                                            </div>
                                            <div className="text-left">
                                                <span className="font-medium text-gray-900">{t('nav.agentConversations')}</span>
                                                <p className="mt-1 text-gray-500 text-xs">{t('nav.agentConversationsDescription')}</p>
                                            </div>
                                        </div>
                                    </Link>
                                )}

                                <button
                                    type="button"
                                    onClick={onLogout}
                                    data-testid="ram-user-menu-sign-out"
                                    className="flex w-full items-center justify-between rounded-lg p-3 transition-colors duration-200 hover:bg-red-50"
                                >
                                    <div className="flex items-center space-x-3">
                                        <div className="rounded-md bg-red-100 p-2 transition-colors">
                                            <LogOut className="h-4 w-4 text-red-600" />
                                        </div>
                                        <div className="text-left">
                                            <span className="font-medium text-red-600">{t('nav.signOut')}</span>
                                            <p className="mt-1 text-red-500 text-xs">{t('nav.signOutDescription')}</p>
                                        </div>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        );
    }

    return (
        <div className="flex items-center gap-1">
            {isImpersonating && onUnimpersonate ? (
                <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="h-8 shrink-0 gap-0 px-2.5"
                    aria-label="Unimpersonate — return to your account"
                    onClick={onUnimpersonate}
                    dataTestId="ram-user-menu-quick-unimpersonate"
                >
                    <span className="flex h-full items-center gap-1.5">
                        <UserRoundX className="size-4 shrink-0" strokeWidth={2.25} />
                        <span className="whitespace-nowrap text-xs font-medium leading-none">Un-Impersonate</span>
                    </span>
                </Button>
            ) : null}
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="outline"
                        size="sm"
                        className={`border-gray-200 hover:border-gray-300 ${className}`}
                        dataTestId="ram-user-menu-trigger"
                        aria-label={isImpersonating ? impersonationTriggerAriaLabel : undefined}
                    >
                        <User className="mr-2 h-4 w-4 shrink-0 text-gray-600" />
                        {isImpersonating ? (
                            <span className="mr-1 hidden min-w-0 max-w-[11rem] flex-col lg:flex">
                                <span className="relative border-amber-500/40 border-t pt-1.5">
                                    <span
                                        aria-hidden
                                        className="absolute top-0 left-0 flex -translate-y-1/2 items-center gap-0.5 bg-background pr-1 font-medium text-[8px] text-amber-700 uppercase leading-none tracking-[0.12em]"
                                    >
                                        <VenetianMask className="h-3 w-3 shrink-0 text-amber-700" strokeWidth={2.25} />
                                        Impersonating
                                    </span>
                                    <span className="block truncate font-medium text-gray-900 text-sm leading-tight">{triggerDisplayName}</span>
                                </span>
                            </span>
                        ) : (
                            <span className="hidden min-w-0 truncate text-gray-700 lg:inline">{t('nav.account')}</span>
                        )}
                        <ChevronDown className="ml-1 h-4 w-4 shrink-0 text-gray-500" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56" dataTestId="ram-user-menu-content">
                    {isImpersonating && impersonatedUserId && onUnimpersonate ? (
                        <>
                            <div className="px-2 py-2">
                                <p className="flex items-center gap-1.5 px-2 pb-1 font-semibold text-amber-950 text-xs uppercase tracking-wide">
                                    <VenetianMask className="h-3.5 w-3.5 shrink-0 text-amber-800" aria-hidden strokeWidth={2} />
                                    Impersonating
                                </p>
                                <div className="flex items-center gap-2 px-2">
                                    <RamAvatar
                                        imageSrc={resolvePersonImageSrcFromUser(impersonatedUser)}
                                        fullname={fullnameForMenuAvatar(impersonatedUser, impersonatedUserId)}
                                        size="compact"
                                    />
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate font-medium text-sm">{impersonatedLabel || impersonatedUserId}</p>
                                        {impersonatedEmail ? <p className="truncate text-muted-foreground text-xs">{impersonatedEmail}</p> : null}
                                    </div>
                                </div>
                                {user?.email ? <p className="px-2 pt-1 text-muted-foreground text-xs">Signed in as {user.email}</p> : null}
                                <Button
                                    type="button"
                                    variant="secondary"
                                    size="sm"
                                    className="mt-2 w-full"
                                    onClick={onUnimpersonate}
                                    dataTestId="ram-user-menu-unimpersonate"
                                >
                                    Unimpersonate
                                </Button>
                            </div>
                            <DropdownMenuSeparator />
                        </>
                    ) : null}
                    {myContactPath ? (
                        <DropdownMenuItem asChild className="cursor-pointer" dataTestId="ram-user-menu-my-profile">
                            <Link to={myContactPath}>
                                <User className="mr-2 h-4 w-4 text-gray-500" />
                                {t('nav.myProfile')}
                            </Link>
                        </DropdownMenuItem>
                    ) : (
                        <DropdownMenuItem disabled className="cursor-not-allowed opacity-60">
                            <User className="mr-2 h-4 w-4 text-gray-500" />
                            {t('nav.noContactProfile')}
                        </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild className="cursor-pointer" dataTestId="ram-user-menu-config">
                        <Link to="/configuration">
                            <Settings className="mr-2 h-4 w-4 text-gray-500" />
                            {t('nav.config')}
                        </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {isAdmin && (
                        <DropdownMenuItem asChild className="cursor-pointer" dataTestId="ram-user-menu-agent-conversations">
                            <Link to="/agents">
                                <Bot className="mr-2 h-4 w-4 text-gray-500" />
                                {t('nav.agentConversations')}
                            </Link>
                        </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={onLogout} className="cursor-pointer text-red-600 focus:text-red-600" dataTestId="ram-user-menu-sign-out">
                        <LogOut className="mr-2 h-4 w-4" />
                        {t('nav.signOut')}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}
